import { Button, NavList, Paragraph } from '@deckee/deck-hand';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROUTE_SETTINGS } from '../../../services/route/route-names';
import {
  transitionToNextRoute,
  transitionToPreviousRoute,
} from '../../../services/route/route-navigation';
import { selectCurrentMember } from '../../../services/store/modules/backend-service';
import {
  consumePendingEmailChangeConfirmToken,
  requestEmailChangeReauth,
} from '../../../services/user/change-email';
import BasicDrawerLayout from '../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../layouts/components/LayoutBody/LayoutBody';
import { atSymbol, notification, tick } from '../../shared/vector-images';
import { trackChangeEmailEvent } from './change-email-events';
import DrawerFlowCard from '../../shared/DrawerFlowCard/DrawerFlowCard';
import DrawerFlowErrorMessage from '../../shared/DrawerFlowCard/components/DrawerFlowErrorMessage';
import DrawerFlowStepRow from '../../shared/DrawerFlowCard/components/DrawerFlowStepRow';

const ChangeEmailIntroPage = () => {
  const history = useHistory();
  const member = useSelector(selectCurrentMember);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackChangeEmailEvent('view', { step: 'intro' });
  }, []);

  useEffect(() => {
    const pendingConfirmToken = consumePendingEmailChangeConfirmToken();
    if (pendingConfirmToken && member) {
      history.replace(
        `/authentication/email-change-confirm?t=${pendingConfirmToken}`,
      );
    }
  }, [history, member]);

  const handleContinue = async () => {
    trackChangeEmailEvent('continue', { step: 'intro' });
    setIsLoading(true);
    setError(null);
    try {
      await requestEmailChangeReauth();
      trackChangeEmailEvent('request reauth', {
        step: 'intro',
        outcome: 'success',
      });
      history.push('/maps/settings/change-email/waiting');
    } catch (e) {
      trackChangeEmailEvent('request reauth', {
        step: 'intro',
        outcome: 'error',
      });
      setError(
        'Something went wrong sending the verification email. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BasicDrawerLayout
      heading="Change email address"
      onBack={() => {
        trackChangeEmailEvent('back', { step: 'intro' });
        transitionToPreviousRoute();
      }}
      onClose={() => {
        trackChangeEmailEvent('close', { step: 'intro' });
        transitionToNextRoute(ROUTE_SETTINGS);
      }}
    >
      <LayoutBody>
        <DrawerFlowCard
          heading="How it works"
          actions={
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={isLoading}
            >
              {isLoading ? 'Sending…' : 'Continue'}
            </Button>
          }
        >
          <Paragraph mb="3" color="darkergrey">
            You&apos;ll need access to both inboxes. The whole process usually
            takes a few minutes.
          </Paragraph>
          <NavList edge grouped>
            <DrawerFlowStepRow
              icon={atSymbol}
              title="Verify your current email"
              description={
                <>
                  We&apos;ll email <strong>{member?.email}</strong>. Open that
                  link to confirm it&apos;s really you.
                </>
              }
            />
            <DrawerFlowStepRow
              icon={notification}
              title="Enter your new email"
              description="Tell us the address you want to use. We'll send a confirmation there."
            />
            <DrawerFlowStepRow
              icon={tick}
              title="Confirm your new inbox"
              description="Open the link in your new inbox while still signed in to DECKEE."
            />
          </NavList>
          {error && <DrawerFlowErrorMessage>{error}</DrawerFlowErrorMessage>}
          <Paragraph fontSize="1" color="darkergrey" mt="3">
            We&apos;ll send a verification link to your current email address
            when you continue.
          </Paragraph>
        </DrawerFlowCard>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default ChangeEmailIntroPage;
